import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Dropdown } from "semantic-ui-react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useNavigate } from "react-router-dom";
import { usePageTranslations } from "hooks/usePageTranslations";
import * as api from "constants/api";
import {
  LOAD_EMPLOYEES,
  SG_GET_ORGANIZATION_GROUP,
  SG_SHARE_DEBRIEF_PDF_REPORT_WITH_EMPLOYEE,
} from "constants/actions";

import {
  extractEmailsFromText,
  normalizeEmail,
  buildDerivedEmailLocalPartsFromName,
  normalizeNameKey,
  normalizeWhitespace,
  parseNameInput,
} from "./shareAuditUtils";

const SharingAudit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const t = usePageTranslations("ProtoType/AutomatedReports/translations");

  const { employees, permissionGroups, userEmployee, organizationId, shareActionByReportId } = useSelector(
    (state) => ({
      employees: Array.isArray(state.employees?.employees) ? state.employees.employees : [],
      permissionGroups: Array.isArray(state.organization_group?.organization_groups_by_type?.permission)
        ? state.organization_group.organization_groups_by_type.permission
        : Array.isArray(state.organization_group?.organization_groups)
        ? state.organization_group.organization_groups
        : [],
      userEmployee: state.employees?.userEmp || state.auth?.employee || null,
      organizationId: Number(state.auth?.organization_id),
      shareActionByReportId: state.debrief_pdf_report?.share_action_by_report_id || {},
    }),
    shallowEqual
  );

  const isAdmin = Number(userEmployee?.account_type) === 5;

  // Ensure we have employees + permission groups available (like AutomatedReports).
  useEffect(() => {
    if (!isAdmin) return;
    if (!employees?.length) dispatch({ type: LOAD_EMPLOYEES });
  }, [dispatch, employees?.length, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!organizationId) return;
    dispatch({
      type: SG_GET_ORGANIZATION_GROUP,
      payload: {
        organization: organizationId,
        group_type: "permission",
        page: 1,
        page_size: 5000,
      },
    });
  }, [dispatch, isAdmin, organizationId]);

  const loginEmployees = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    return list.filter((employee) => {
      const hasLoginFlag =
        employee.enable_login === true ||
        employee.enable_login === 1 ||
        employee.enable_login === "1";
      const hasPermissionsArray =
        Array.isArray(employee.new_permission) &&
        employee.new_permission.some(
          (p) =>
            (p?.name_id || p?.name) === "has_login" ||
            (p?.name_id || p?.name) === "hasLogin" ||
            (p?.value_id || p?.value) === "has_login"
        );
      const hasLoginAlt =
        employee.has_login === true ||
        employee.has_login === 1 ||
        employee.has_login === "1";
      return hasLoginFlag || hasPermissionsArray || hasLoginAlt;
    });
  }, [employees]);

  const loginEmployeeOptions = useMemo(() => {
    return (loginEmployees || [])
      .map((emp) => {
        const id = Number(emp?.id);
        if (!Number.isFinite(id) || id <= 0) return null;
        const name = `${emp?.first_name || ""} ${emp?.last_name || ""}`.trim();
        const text = name ? `${name} (${emp?.email || ""})` : emp?.email || `Employee ${id}`;
        return { key: id, value: id, text };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.text).localeCompare(String(b.text)));
  }, [loginEmployees]);

  const permissionGroupsByManagerEmail = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(permissionGroups) ? permissionGroups : [];
    list.forEach((g) => {
      const groupId = Number(g?.id);
      if (!Number.isFinite(groupId) || groupId <= 0) return;
      const email = normalizeEmail(g?.manager?.email);
      if (!email) return;
      const existing = map.get(email);
      if (Array.isArray(existing)) existing.push(groupId);
      else map.set(email, [groupId]);
    });
    return map;
  }, [permissionGroups]);

  const permissionGroupsManagerEmailByGroupId = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(permissionGroups) ? permissionGroups : [];
    list.forEach((g) => {
      const groupId = Number(g?.id);
      if (!Number.isFinite(groupId) || groupId <= 0) return;
      const email = normalizeEmail(g?.manager?.email);
      if (!email) return;
      map.set(groupId, email);
    });
    return map;
  }, [permissionGroups]);

  const permissionGroupsManagerLabelByEmail = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(permissionGroups) ? permissionGroups : [];
    list.forEach((g) => {
      const mgr = g?.manager;
      const email = normalizeEmail(mgr?.email);
      if (!email) return;
      if (map.has(email)) return;
      const name =
        mgr && typeof mgr === "object"
          ? normalizeWhitespace(`${mgr.first_name || ""} ${mgr.last_name || ""}`.trim())
          : "";
      map.set(email, name ? `${name} (${email})` : email);
    });
    return map;
  }, [permissionGroups]);

  const permissionGroupsByManagerNameKey = useMemo(() => {
    // nameKey -> { groupIds: number[], managerEmails: Set<string> }
    const map = new Map();
    const list = Array.isArray(permissionGroups) ? permissionGroups : [];
    list.forEach((g) => {
      const groupId = Number(g?.id);
      if (!Number.isFinite(groupId) || groupId <= 0) return;
      const mgr = g?.manager;
      if (!mgr || typeof mgr !== "object") return;
      const first = normalizeWhitespace(mgr.first_name || "");
      const last = normalizeWhitespace(mgr.last_name || "");
      const mgrEmail = normalizeEmail(mgr.email);
      if (!first || !last) return;

      const base = parseNameInput(`${first} ${last}`);
      const keys = [
        normalizeNameKey(base.first || first, base.last || last),
        base.nickname ? normalizeNameKey(base.nickname, base.last || last) : "",
      ].filter(Boolean);

      keys.forEach((key) => {
        const existing = map.get(key);
        if (!existing) {
          map.set(key, {
            groupIds: [groupId],
            managerEmails: new Set(mgrEmail ? [mgrEmail] : []),
          });
          return;
        }
        existing.groupIds.push(groupId);
        if (mgrEmail) existing.managerEmails.add(mgrEmail);
      });
    });

    const finalized = new Map();
    map.forEach((v, k) => {
      finalized.set(k, {
        groupIds: v.groupIds,
        managerEmails: Array.from(v.managerEmails.values()),
      });
    });
    return finalized;
  }, [permissionGroups]);

  const permissionGroupsKnownDomains = useMemo(() => {
    const domains = new Set();
    const list = Array.isArray(permissionGroups) ? permissionGroups : [];
    list.forEach((g) => {
      const email = normalizeEmail(g?.manager?.email);
      if (!email || !email.includes("@")) return;
      const domain = email.split("@")[1] || "";
      if (domain) domains.add(domain);
    });
    return Array.from(domains.values());
  }, [permissionGroups]);

  const managerGroupOptions = useMemo(() => {
    const list = Array.isArray(permissionGroups) ? permissionGroups : [];
    return list
      .map((g) => {
        const id = Number(g?.id);
        if (!Number.isFinite(id) || id <= 0) return null;
        const mgr = g?.manager;
        const managerLabel =
          mgr && typeof mgr === "object"
            ? `${mgr.first_name || ""} ${mgr.last_name || ""}`.trim() || mgr.email
            : null;
        const email = mgr && typeof mgr === "object" ? String(mgr.email || "").trim() : "";
        const base = String(g?.name || g?.title || managerLabel || "").trim();
        const text = [base, email].filter(Boolean).join(" ").trim() || t("fallbackGroupNumber", { id });
        return { key: id, value: id, text };
      })
      .filter(Boolean)
      .sort((a, b) => String(a.text).localeCompare(String(b.text)));
  }, [permissionGroups, t]);

  // Page state
  const [targetEmployeeId, setTargetEmployeeId] = useState(null);

  const [loadingTargetAccess, setLoadingTargetAccess] = useState(false);
  const [targetAccessibleReportsById, setTargetAccessibleReportsById] = useState({});
  const [targetAccessibleGroupIdSet, setTargetAccessibleGroupIdSet] = useState(new Set());
  const [targetAccessibleManagerEmailSet, setTargetAccessibleManagerEmailSet] = useState(new Set());
  const [targetAccessLoadedAt, setTargetAccessLoadedAt] = useState(null);
  const targetAccessTokenRef = useRef(0);

  const [pasteText, setPasteText] = useState("");
  const [pasteResult, setPasteResult] = useState(null);

  const [selectedManagerGroupIds, setSelectedManagerGroupIds] = useState([]);
  const [lastLoadedManagerGroupIds, setLastLoadedManagerGroupIds] = useState([]);
  const [loadedReportsById, setLoadedReportsById] = useState({});
  const [selectedReportIds, setSelectedReportIds] = useState([]);

  const [loadingManagerReports, setLoadingManagerReports] = useState({ active: false, done: 0, total: 0, message: null });

  const [shareSendState, setShareSendState] = useState({
    active: false,
    phase: null, // "queueing" | "processing" | null
    total: 0,
    sent: 0,
    items: [], // [{ reportId, label, status: "pending" | "sent" }]
  });
  const [shareLastBatchSummary, setShareLastBatchSummary] = useState(null);

  // Reset state when target changes
  useEffect(() => {
    targetAccessTokenRef.current += 1;
    setLoadingTargetAccess(false);
    setTargetAccessibleReportsById({});
    setTargetAccessibleGroupIdSet(new Set());
    setTargetAccessibleManagerEmailSet(new Set());
    setTargetAccessLoadedAt(null);
    setPasteText("");
    setPasteResult(null);
    setSelectedManagerGroupIds([]);
    setLastLoadedManagerGroupIds([]);
    setLoadedReportsById({});
    setSelectedReportIds([]);
    setLoadingManagerReports({ active: false, done: 0, total: 0, message: null });
    setShareSendState({ active: false, phase: null, total: 0, sent: 0, items: [] });
    setShareLastBatchSummary(null);
  }, [targetEmployeeId]);

  // Keep UI counts in sync even when the manager-group selection changes (e.g. after resolving ambiguous rows).
  useEffect(() => {
    setPasteResult((prev) => {
      if (!prev) return prev;
      const resolvedManagerEmails = Array.isArray(prev.resolvedManagerEmails) ? prev.resolvedManagerEmails : [];
      const selectedManagerGroupsCount = Array.isArray(selectedManagerGroupIds) ? selectedManagerGroupIds.length : 0;
      const resolvedManagersCount = resolvedManagerEmails.length;
      if (prev.selectedManagerGroupsCount === selectedManagerGroupsCount && prev.resolvedManagersCount === resolvedManagersCount) {
        return prev;
      }
      return {
        ...prev,
        selectedManagerGroupsCount,
        resolvedManagersCount,
      };
    });
  }, [selectedManagerGroupIds]);

  const fetchAllReports = useCallback(async ({ baseQuery, pageSize = 500, maxTotal = 5000, tokenRef }) => {
    const size = Number(pageSize) > 0 ? Number(pageSize) : 500;
    const cap = Number(maxTotal) > 0 ? Number(maxTotal) : 5000;
    const collected = [];
    let page = 1;
    let hasNext = true;

    while (hasNext && collected.length < cap) {
      if (tokenRef && tokenRef.current == null) break;
      const qs = `${baseQuery}&page=${encodeURIComponent(page)}&page_size=${encodeURIComponent(size)}`;
      // eslint-disable-next-line no-await-in-loop
      const json = await api.GET_DEBRIEF_PDF_REPORT(qs);
      const data = json?.data;
      const results = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
      collected.push(...results);
      if (collected.length >= cap) break;

      const nextUrl = typeof data?.next === "string" ? data.next : data?.links?.next;
      if (nextUrl) {
        page += 1;
        hasNext = true;
      } else {
        hasNext = results.length === size;
        page += 1;
      }
    }

    return collected.slice(0, cap);
  }, []);

  const handleLoadTargetAccess = useCallback(async () => {
    if (!isAdmin) return;
    const id = Number(targetEmployeeId);
    if (!Number.isFinite(id) || id <= 0) return;
    if (loadingTargetAccess) return;

    const token = (targetAccessTokenRef.current += 1);
    setLoadingTargetAccess(true);
    try {
      const baseQuery = [
        "report_type=3",
        organizationId ? `organization=${encodeURIComponent(organizationId)}` : null,
        `employee_id=${encodeURIComponent(id)}`,
      ]
        .filter(Boolean)
        .join("&");

      const list = await fetchAllReports({
        baseQuery,
        pageSize: 500,
        maxTotal: 8000,
      });
      if (targetAccessTokenRef.current !== token) return;

      const byId = {};
      const groupIds = new Set();
      const managerEmails = new Set();
      (list || []).forEach((r) => {
        const rid = Number(r?.id);
        if (!Number.isFinite(rid) || rid <= 0) return;
        byId[rid] = r;

        const og = Number(r?.organization_group);
        if (Number.isFinite(og) && og > 0) {
          groupIds.add(og);
        }

        // Some report payloads include the manager email under generated metadata.
        const metaManagerEmail = normalizeEmail(r?.additional_info?.generated_data?.metadata?.group_name);
        if (metaManagerEmail) {
          managerEmails.add(metaManagerEmail);
        }
      });

      setTargetAccessibleReportsById(byId);
      setTargetAccessibleGroupIdSet(groupIds);
      setTargetAccessibleManagerEmailSet(managerEmails);
      setTargetAccessLoadedAt(Date.now());
      addToast(t("sharingAuditLoadedAccessToast", { count: Object.keys(byId).length }), {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (e) {
      addToast(t("sharingAuditLoadAccessFailed"), { appearance: "error", autoDismiss: true });
    } finally {
      if (targetAccessTokenRef.current === token) setLoadingTargetAccess(false);
    }
  }, [addToast, fetchAllReports, isAdmin, loadingTargetAccess, organizationId, t, targetEmployeeId]);

  const toCandidateManagers = useCallback(
    (groupIds) => {
      const ids = Array.isArray(groupIds) ? groupIds : [];
      const byEmail = new Map(); // managerEmail -> number[]
      ids.forEach((gid) => {
        const email = permissionGroupsManagerEmailByGroupId.get(Number(gid)) || "";
        const key = email || `__unknown__:${gid}`;
        const existing = byEmail.get(key);
        if (Array.isArray(existing)) existing.push(Number(gid));
        else byEmail.set(key, [Number(gid)]);
      });

      return Array.from(byEmail.entries())
        .map(([managerEmail, groupIdsForEmail]) => {
          const label =
            managerEmail && !managerEmail.startsWith("__unknown__")
              ? permissionGroupsManagerLabelByEmail.get(managerEmail) || managerEmail
              : t("unknownLabel");
          return {
            managerEmail,
            label,
            groupIds: groupIdsForEmail.filter((v) => Number.isFinite(v) && v > 0),
          };
        })
        .filter((x) => x.groupIds.length > 0);
    },
    [permissionGroupsManagerEmailByGroupId, permissionGroupsManagerLabelByEmail, t]
  );

  const handleApplyPaste = useCallback(() => {
    const raw = String(pasteText || "");
    const rawLines = raw.split(/\r?\n/);

    const inputs = [];
    rawLines.forEach((line) => {
      const cleaned = normalizeWhitespace(line);
      if (!cleaned) return;
      const emailsInLine = extractEmailsFromText(cleaned);
      if (emailsInLine.length > 0) {
        emailsInLine.forEach((email) => inputs.push({ raw: email, type: "email" }));
      } else {
        inputs.push({ raw: cleaned, type: "name" });
      }
    });

    const uniqueMap = new Map();
    inputs.forEach((it) => {
      const key = it.type === "email" ? normalizeEmail(it.raw) : normalizeWhitespace(it.raw);
      if (!key) return;
      if (!uniqueMap.has(key)) uniqueMap.set(key, it);
    });
    const uniqueInputs = Array.from(uniqueMap.values());

    if (!uniqueInputs.length) {
      setPasteResult({
        totalUnique: 0,
        matched: 0,
        ambiguous: 0,
        unmatched: 0,
        alreadySelected: 0,
        unmatchedInputs: [],
        ambiguousItems: [],
        resolvedManagerEmails: [],
        includedManagerEmails: [],
        missingManagerEmails: [],
      });
      return;
    }

    const matchedGroupIds = [];
    const ambiguousItems = [];
    const unmatchedInputs = [];

    const tryMatchEmail = (email) => {
      const groupIds = permissionGroupsByManagerEmail.get(email);
      if (Array.isArray(groupIds) && groupIds.length > 0) {
        return { kind: "matched", reason: "exact_email", groupIds };
      }
      return { kind: "unmatched" };
    };

    const tryMatchName = (nameRaw) => {
      const parsed = parseNameInput(nameRaw);
      const baseKey = normalizeNameKey(parsed.first, parsed.last);
      const nickKey = parsed.nickname ? normalizeNameKey(parsed.nickname, parsed.last) : "";
      const keys = [baseKey, nickKey].filter(Boolean);
      if (!keys.length) return { kind: "unmatched" };

      const exactCandidates = [];
      keys.forEach((key) => {
        const entry = permissionGroupsByManagerNameKey.get(key);
        if (!entry?.groupIds?.length) return;
        exactCandidates.push(...entry.groupIds);
      });
      const exactUnique = Array.from(
        new Set(exactCandidates.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0))
      );

      if (exactUnique.length > 0) {
        const managers = toCandidateManagers(exactUnique);
        if (managers.length === 1) {
          return { kind: "matched", reason: "exact_name", groupIds: managers[0].groupIds };
        }
        return { kind: "ambiguous", reason: "exact_name", candidateManagers: managers };
      }

      const expandDomains = (domains) => {
        const base = Array.isArray(domains) ? domains : [];
        const out = new Set(base.filter(Boolean).map((d) => String(d).toLowerCase().trim()));
        base.forEach((d) => {
          const s = String(d || "").toLowerCase().trim();
          if (s.endsWith(".com")) out.add(s.replace(/\.com$/, ".ca"));
          if (s.endsWith(".ca")) out.add(s.replace(/\.ca$/, ".com"));
        });
        const userDomain = normalizeEmail(userEmployee?.email).split("@")[1];
        if (userDomain) out.add(String(userDomain).toLowerCase().trim());
        return Array.from(out.values()).filter(Boolean);
      };

      const domains = expandDomains(permissionGroupsKnownDomains);
      const uniqueLocalParts = buildDerivedEmailLocalPartsFromName(nameRaw);

      const derivedGroupIds = [];
      const derivedEmails = [];
      domains.forEach((domain) => {
        uniqueLocalParts.forEach((lp) => {
          const email = normalizeEmail(`${lp}@${domain}`);
          const groupIds = permissionGroupsByManagerEmail.get(email);
          if (Array.isArray(groupIds) && groupIds.length > 0) {
            derivedEmails.push(email);
            derivedGroupIds.push(...groupIds);
          }
        });
      });
      const derivedUnique = Array.from(
        new Set(derivedGroupIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0))
      );
      const derivedEmailUnique = Array.from(new Set(derivedEmails));

      if (derivedUnique.length > 0) {
        const managers = toCandidateManagers(derivedUnique);
        if (managers.length === 1) {
          return { kind: "matched", reason: "derived_email", derivedEmails: derivedEmailUnique, groupIds: managers[0].groupIds };
        }
        return { kind: "ambiguous", reason: "derived_email", derivedEmails: derivedEmailUnique, candidateManagers: managers };
      }

      return { kind: "unmatched" };
    };

    uniqueInputs.forEach((it) => {
      if (it.type === "email") {
        const email = normalizeEmail(it.raw);
        const res = tryMatchEmail(email);
        if (res.kind === "matched") matchedGroupIds.push(...res.groupIds);
        else unmatchedInputs.push(email);
        return;
      }

      const nameRaw = String(it.raw || "");
      const res = tryMatchName(nameRaw);
      if (res.kind === "matched") {
        matchedGroupIds.push(...res.groupIds);
      } else if (res.kind === "ambiguous") {
        const options = (res.candidateManagers || []).map((m) => ({
          key: m.managerEmail || m.label,
          value: m.managerEmail || m.label,
          text: m.label,
          _groupIds: m.groupIds,
        }));
        ambiguousItems.push({
          input: nameRaw,
          reason: res.reason,
          derivedEmails: res.derivedEmails || [],
          options,
          selectedValue: options[0]?.value || "",
          resolved: false,
        });
      } else {
        unmatchedInputs.push(nameRaw);
      }
    });

    const matchedUniqueGroupIds = Array.from(
      new Set(matchedGroupIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0))
    );
    const matchedInputsCount = Math.max(0, uniqueInputs.length - ambiguousItems.length - unmatchedInputs.length);

    // Build resolved manager emails set from matched groups (NOT from the "load managers" selection).
    const resolvedManagerEmails = Array.from(
      new Set(
        matchedUniqueGroupIds
          .map((gid) => permissionGroupsManagerEmailByGroupId.get(Number(gid)))
          .map(normalizeEmail)
          .filter(Boolean)
      )
    );

    // A manager is "included" if the target has access to at least one report in any of that manager's permission groups,
    // or if the report payload included their manager email in generated metadata.
    const includedManagerEmails = resolvedManagerEmails.filter((email) => {
      if (targetAccessibleManagerEmailSet.has(email)) return true;
      const gids = permissionGroupsByManagerEmail.get(email) || [];
      return Array.isArray(gids) && gids.some((gid) => targetAccessibleGroupIdSet.has(Number(gid)));
    });
    const missingManagerEmails = resolvedManagerEmails.filter((email) => !includedManagerEmails.includes(email));

    // Only auto-load missing managers into the "Load manager reports" section.
    const missingGroupIds = Array.from(
      new Set(
        missingManagerEmails
          .flatMap((email) => permissionGroupsByManagerEmail.get(email) || [])
          .map((v) => Number(v))
          .filter((v) => Number.isFinite(v) && v > 0)
      )
    );
    const prevIds = Array.isArray(selectedManagerGroupIds)
      ? selectedManagerGroupIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];
    const alreadySelected = missingGroupIds.filter((id) => prevIds.includes(id)).length;
    setSelectedManagerGroupIds(missingGroupIds);

    setPasteResult({
      totalUnique: uniqueInputs.length,
      matchedInputs: matchedInputsCount,
      ambiguous: ambiguousItems.length,
      unmatched: unmatchedInputs.length,
      alreadySelected,
      unmatchedInputs,
      ambiguousItems,
      resolvedManagerEmails,
      resolvedManagersCount: resolvedManagerEmails.length,
      selectedManagerGroupsCount: missingGroupIds.length,
      includedManagerEmails,
      missingManagerEmails,
    });
  }, [
    pasteText,
    permissionGroupsByManagerEmail,
    permissionGroupsByManagerNameKey,
    permissionGroupsKnownDomains,
    permissionGroupsManagerEmailByGroupId,
    selectedManagerGroupIds,
    t,
    targetAccessibleGroupIdSet,
    targetAccessibleManagerEmailSet,
    toCandidateManagers,
    userEmployee?.email,
  ]);

  const hasUnresolvedAmbiguous = useMemo(() => {
    const items = pasteResult?.ambiguousItems;
    return Array.isArray(items) && items.some((it) => it && !it.resolved);
  }, [pasteResult?.ambiguousItems]);

  const handlePasteAmbiguousPick = (idx, nextValue) => {
    setPasteResult((prev) => {
      const items = Array.isArray(prev?.ambiguousItems) ? prev.ambiguousItems : [];
      if (idx < 0 || idx >= items.length) return prev;
      const nextItems = items.map((it, i) => (i === idx ? { ...it, selectedValue: nextValue } : it));
      return { ...prev, ambiguousItems: nextItems };
    });
  };

  const handlePasteAmbiguousApply = (idx) => {
    setPasteResult((prev) => {
      const items = Array.isArray(prev?.ambiguousItems) ? prev.ambiguousItems : [];
      if (idx < 0 || idx >= items.length) return prev;
      const item = items[idx];
      if (!item || item.resolved) return prev;

      const selected = (item.options || []).find((o) => o?.value === item.selectedValue);
      const groupIds = Array.isArray(selected?._groupIds) ? selected._groupIds : [];

      // Determine manager emails for this ambiguous resolution.
      const managerEmails = Array.from(
        new Set(
          groupIds
            .map((gid) => normalizeEmail(permissionGroupsManagerEmailByGroupId.get(Number(gid))))
            .filter(Boolean)
        )
      );

      // Only add to the "load managers" selection if the resolved manager is actually missing.
      const isIncluded = (email) => {
        if (targetAccessibleManagerEmailSet.has(email)) return true;
        const gids = permissionGroupsByManagerEmail.get(email) || [];
        return Array.isArray(gids) && gids.some((gid) => targetAccessibleGroupIdSet.has(Number(gid)));
      };
      const missingEmailsFromResolution = managerEmails.filter((email) => !isIncluded(email));

      if (missingEmailsFromResolution.length) {
        const idsToAdd = Array.from(
          new Set(
            missingEmailsFromResolution
              .flatMap((email) => permissionGroupsByManagerEmail.get(email) || [])
              .map((v) => Number(v))
              .filter((v) => Number.isFinite(v) && v > 0)
          )
        );
        if (idsToAdd.length) {
          setSelectedManagerGroupIds((prevIds) => {
            const base = Array.isArray(prevIds)
              ? prevIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
              : [];
            const nextSet = new Set(base);
            idsToAdd.forEach((v) => nextSet.add(v));
            return Array.from(nextSet.values());
          });
        }
      }

      const nextItems = items.map((it, i) => (i === idx ? { ...it, resolved: true } : it));
      const unresolvedCount = nextItems.filter((it) => !it?.resolved).length;

      const prevResolved = Array.isArray(prev?.resolvedManagerEmails) ? prev.resolvedManagerEmails : [];
      const mergedResolved = Array.from(new Set([...prevResolved, ...managerEmails]));

      const nextIncluded = mergedResolved.filter((email) => isIncluded(email));
      const nextMissing = mergedResolved.filter((email) => !nextIncluded.includes(email));

      // Update matched count now that one ambiguous line has been resolved.
      const totalUnique = Number(prev?.totalUnique) || 0;
      const unmatched = Number(prev?.unmatched) || 0;
      const nextMatchedInputs = Math.max(0, totalUnique - unmatched - unresolvedCount);

      return {
        ...prev,
        ambiguousItems: nextItems,
        ambiguous: unresolvedCount,
        matchedInputs: nextMatchedInputs,
        resolvedManagerEmails: mergedResolved,
        includedManagerEmails: nextIncluded,
        missingManagerEmails: nextMissing,
      };
    });
  };

  const mergeLoadedReports = (reports) => {
    if (!Array.isArray(reports) || reports.length === 0) return;
    setLoadedReportsById((prev) => {
      const next = { ...(prev || {}) };
      reports.forEach((r) => {
        const id = Number(r?.id);
        if (!Number.isFinite(id) || id <= 0) return;
        next[id] = r;
      });
      return next;
    });
  };

  const handleLoadReportsFromManagers = async ({ onlyMissing } = {}) => {
    if (!isAdmin) return;
    if (loadingManagerReports.active) return;
    if (hasUnresolvedAmbiguous) return;

    const ids = Array.isArray(selectedManagerGroupIds)
      ? selectedManagerGroupIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];

    const groupIds = (() => {
      if (!onlyMissing) return ids;
      const missingEmails = Array.isArray(pasteResult?.missingManagerEmails) ? pasteResult.missingManagerEmails : [];
      if (!missingEmails.length) return [];
      const out = [];
      ids.forEach((gid) => {
        const email = normalizeEmail(permissionGroupsManagerEmailByGroupId.get(gid));
        if (email && missingEmails.includes(email)) out.push(gid);
      });
      return out;
    })();

    if (!groupIds.length) return;

    setLastLoadedManagerGroupIds(groupIds);
    setLoadingManagerReports({
      active: true,
      done: 0,
      total: groupIds.length,
      message: t("sharingAuditLoadingManagerReports"),
    });

    const targetReportIdsSet = new Set(Object.keys(targetAccessibleReportsById || {}).map((k) => Number(k)));
    const newlyNotAccessible = [];

    try {
      for (let idx = 0; idx < groupIds.length; idx += 1) {
        const gid = groupIds[idx];
        // eslint-disable-next-line no-await-in-loop
        const res = await api.GET_DEBRIEF_PDF_REPORT(
          `report_type=3&organization_group=${encodeURIComponent(gid)}&page=1&page_size=500`
        );
        const data = res?.data;
        const list = Array.isArray(data) ? data : data?.results || [];
        mergeLoadedReports(list);

        (list || []).forEach((r) => {
          const rid = Number(r?.id);
          if (!Number.isFinite(rid) || rid <= 0) return;
          if (!targetReportIdsSet.has(rid)) newlyNotAccessible.push(rid);
        });

        setLoadingManagerReports((prev) => ({
          ...prev,
          done: Math.min((prev?.done || 0) + 1, groupIds.length),
        }));

        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 45));
      }
    } catch (e) {
      addToast(t("sharingAuditLoadManagerReportsFailed"), { appearance: "warning", autoDismiss: true });
    } finally {
      setLoadingManagerReports({ active: false, done: 0, total: 0, message: null });
    }

    // Default-select newly loaded reports that the target does NOT already have.
    const unique = Array.from(new Set(newlyNotAccessible)).filter((id) => Number.isFinite(id) && id > 0);
    if (unique.length) {
      setSelectedReportIds((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        const merged = new Set([...base, ...unique]);
        return Array.from(merged.values());
      });
    }
  };

  const loadedReports = useMemo(() => {
    const values = Object.values(loadedReportsById || {});
    return values
      .filter(Boolean)
      .sort((a, b) => {
        const dateA = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });
  }, [loadedReportsById]);

  const targetAccessibleReportIdSet = useMemo(() => {
    return new Set(Object.keys(targetAccessibleReportsById || {}).map((k) => Number(k)).filter((v) => Number.isFinite(v) && v > 0));
  }, [targetAccessibleReportsById]);

  const reportBuckets = useMemo(() => {
    const accessible = [];
    const notAccessible = [];
    loadedReports.forEach((r) => {
      const id = Number(r?.id);
      if (!Number.isFinite(id) || id <= 0) return;
      if (targetAccessibleReportIdSet.has(id)) accessible.push(r);
      else notAccessible.push(r);
    });
    return { accessible, notAccessible };
  }, [loadedReports, targetAccessibleReportIdSet]);

  const toggleReport = (reportId) => {
    const id = Number(reportId);
    if (!Number.isFinite(id) || id <= 0) return;
    setSelectedReportIds((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    });
  };

  const getReportDisplayName = (report) => {
    const id = report?.id;
    return report?.report_name || report?.org_template_name || t("fallbackReportNumber", { id });
  };

  const getReportTemplateLabel = (report) => {
    return (
      report?.org_template_name ||
      report?.template_name ||
      report?.pdf_template_report_name ||
      report?.pdf_template_report?.name ||
      (report?.pdf_template_report ? t("fallbackTemplateNumber", { id: report.pdf_template_report }) : null) ||
      "—"
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch (e) {
      return dateString;
    }
  };

  const handleApplyShares = async () => {
    if (!isAdmin) return;
    const targetId = Number(targetEmployeeId);
    if (!Number.isFinite(targetId) || targetId <= 0) return;
    if (shareSendState.active) return;

    const ids = Array.isArray(selectedReportIds)
      ? selectedReportIds.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
      : [];
    const toShare = ids.filter((rid) => !targetAccessibleReportIdSet.has(rid));
    if (!toShare.length) return;

    const items = toShare.map((rid) => {
      const report = loadedReportsById?.[rid] || targetAccessibleReportsById?.[rid] || { id: rid };
      return { reportId: rid, label: getReportDisplayName(report), status: "pending" };
    });

    setShareSendState({
      active: true,
      phase: "queueing",
      total: items.length,
      sent: 0,
      items,
    });
    setShareLastBatchSummary(null);

    for (let idx = 0; idx < items.length; idx += 1) {
      const item = items[idx];
      if (!item?.reportId) continue;
      dispatch({
        type: SG_SHARE_DEBRIEF_PDF_REPORT_WITH_EMPLOYEE,
        payload: { id: item.reportId, employee_ids: [targetId], skip_refresh: true },
      });

      setShareSendState((prev) => {
        const nextItems = (prev.items || []).map((it) =>
          it.reportId === item.reportId ? { ...it, status: "sent" } : it
        );
        return {
          ...prev,
          active: true,
          sent: Math.min((prev.sent || 0) + 1, prev.total || items.length),
          items: nextItems,
        };
      });

      // Yield so the UI shows progress while queueing.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 80));
    }

    addToast(t("sharingAuditQueuedSharesToast", { count: items.length }), {
      appearance: "success",
      autoDismiss: true,
    });

    setShareSendState((prev) => ({
      ...prev,
      active: true,
      phase: "processing",
    }));
  };

  const shareBatchProgress = useMemo(() => {
    if (!shareSendState?.active) return null;
    const items = Array.isArray(shareSendState.items) ? shareSendState.items : [];
    if (!items.length) return { total: 0, completed: 0, pending: 0, errors: 0 };

    let completed = 0;
    let pending = 0;
    let errors = 0;

    items.forEach((it) => {
      const reportId = Number(it?.reportId);
      if (!Number.isFinite(reportId) || reportId <= 0) return;
      const actionState = shareActionByReportId?.[reportId];

      if (!actionState) {
        pending += 1;
        return;
      }
      if (actionState.pending) {
        pending += 1;
        return;
      }
      if (actionState.error) {
        errors += 1;
        completed += 1;
        return;
      }
      completed += 1;
    });

    return { total: items.length, completed, pending, errors };
  }, [shareActionByReportId, shareSendState?.active, shareSendState?.items]);

  useEffect(() => {
    if (!shareSendState?.active) return;
    if (shareSendState.phase !== "processing") return;
    if (!shareBatchProgress) return;
    if (shareBatchProgress.total <= 0) return;
    if (shareBatchProgress.pending > 0) return;

    const errs = Number(shareBatchProgress.errors) || 0;
    const tot = Number(shareBatchProgress.total) || 0;

    setShareLastBatchSummary({ total: tot, errors: errs, completedAt: Date.now() });
    addToast(t("groupShareBatchCompletedToast", { total: tot, errors: errs }), {
      appearance: errs > 0 ? "warning" : "success",
      autoDismiss: true,
    });
    setShareSendState({ active: false, phase: null, total: 0, sent: 0, items: [] });
  }, [addToast, shareBatchProgress, shareSendState?.active, shareSendState?.phase, t]);

  if (!isAdmin) {
    return (
      <Page>
        <TopRow>
          <BackButton type="button" onClick={() => navigate(-1)}>
            {t("back")}
          </BackButton>
        </TopRow>
        <Card>
          <Title>{t("sharingAuditTitle")}</Title>
          <Muted>{t("sharingAuditAdminOnly")}</Muted>
        </Card>
      </Page>
    );
  }

  const targetAccessCount = Object.keys(targetAccessibleReportsById || {}).length;

  return (
    <Page>
      <TopRow>
        <BackButton type="button" onClick={() => navigate("/leader/audit/automated-reports")}>
          {t("backToAutomatedReports")}
        </BackButton>
      </TopRow>

      <Header>
        <Title>{t("sharingAuditTitle")}</Title>
        <Subtitle>{t("sharingAuditSubtitle")}</Subtitle>
      </Header>

      <Card>
        <SectionTitle>{t("sharingAuditTargetUser")}</SectionTitle>
        <Row>
          <WideDropdown
            search
            selection
            fluid
            placeholder={t("groupShareSelectUserPlaceholder")}
            options={loginEmployeeOptions}
            value={targetEmployeeId || ""}
            onChange={(e, { value }) => setTargetEmployeeId(value || null)}
          />
          <PrimaryButton
            type="button"
            onClick={handleLoadTargetAccess}
            disabled={!targetEmployeeId || loadingTargetAccess}
          >
            {loadingTargetAccess ? t("sharingAuditLoadingAccess") : t("sharingAuditLoadAccess")}
          </PrimaryButton>
        </Row>

        <Meta>
          {targetEmployeeId ? (
            <>
              <div>{t("sharingAuditAccessCount", { count: targetAccessCount })}</div>
              <div>
                {targetAccessLoadedAt
                  ? t("sharingAuditLoadedAt", { when: new Date(targetAccessLoadedAt).toLocaleString() })
                  : t("sharingAuditNotLoadedYet")}
              </div>
            </>
          ) : (
            <div>{t("sharingAuditPickUserHint")}</div>
          )}
        </Meta>
      </Card>

      <Card>
        <SectionTitle>{t("sharingAuditPasteManagersTitle")}</SectionTitle>
        <Muted>{t("groupSharePasteHint")}</Muted>

        <Textarea
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          placeholder={t("pasteEmailsPlaceholder")}
          disabled={!targetEmployeeId}
        />

        <Row style={{ marginTop: 10 }}>
          <SecondaryButton
            type="button"
            onClick={() => {
              setPasteText("");
              setPasteResult(null);
              setSelectedManagerGroupIds([]);
            }}
            disabled={!pasteText.trim()}
          >
            {t("pasteEmailsClear")}
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleApplyPaste}
            disabled={!pasteText.trim() || !targetEmployeeId || loadingTargetAccess}
          >
            {t("pasteEmailsApply")}
          </PrimaryButton>
        </Row>

        {pasteResult && (
          <ResultBox
            hasError={
              pasteResult.totalUnique > 0 &&
              (pasteResult.matchedInputs || 0) === 0 &&
              (pasteResult.ambiguous || 0) === 0
            }
          >
            <ResultLine>
              {pasteResult.totalUnique === 0
                ? t("groupSharePasteNoInputs")
                : t("sharingAuditPasteResultSummary", {
                    matched: pasteResult.matchedInputs || 0,
                    total: pasteResult.totalUnique || 0,
                    ambiguous: pasteResult.ambiguous || 0,
                    unmatched: pasteResult.unmatched || 0,
                    alreadySelected: pasteResult.alreadySelected || 0,
                  })}
            </ResultLine>

            <Definitions>
              <DefRow>
                <DefLabel>{t("sharingAuditDefMatchedLabel")}</DefLabel>
                <DefText>{t("sharingAuditDefMatchedText")}</DefText>
              </DefRow>
              <DefRow>
                <DefLabel>{t("sharingAuditDefAmbiguousLabel")}</DefLabel>
                <DefText>{t("sharingAuditDefAmbiguousText")}</DefText>
              </DefRow>
              <DefRow>
                <DefLabel>{t("sharingAuditDefUnmatchedLabel")}</DefLabel>
                <DefText>{t("sharingAuditDefUnmatchedText")}</DefText>
              </DefRow>
              <DefRow>
                <DefLabel>{t("sharingAuditDefMissingLabel")}</DefLabel>
                <DefText>{t("sharingAuditDefMissingText")}</DefText>
              </DefRow>
            </Definitions>

            <CountsRow>
              <CountChip>{t("sharingAuditResolvedManagersCount", { count: pasteResult.resolvedManagersCount || 0 })}</CountChip>
              <CountChip>
                {t("sharingAuditSelectedManagerGroupsCount", { count: pasteResult.selectedManagerGroupsCount || 0 })}
              </CountChip>
            </CountsRow>

            {Array.isArray(pasteResult.missingManagerEmails) && pasteResult.missingManagerEmails.length > 0 && (
              <MissingBox>
                <MissingTitle>
                  {t("sharingAuditMissingManagers", { count: pasteResult.missingManagerEmails.length })}
                </MissingTitle>
                <ChipRow>
                  {pasteResult.missingManagerEmails.slice(0, 12).map((email) => (
                    <Chip key={email} $variant="missing">
                      {permissionGroupsManagerLabelByEmail.get(email) || email}
                    </Chip>
                  ))}
                  {pasteResult.missingManagerEmails.length > 12 && (
                    <MoreText>
                      {t("pasteEmailsMoreUnmatched", { count: pasteResult.missingManagerEmails.length - 12 })}
                    </MoreText>
                  )}
                </ChipRow>
              </MissingBox>
            )}

            {Array.isArray(pasteResult.includedManagerEmails) && pasteResult.includedManagerEmails.length > 0 && (
              <MissingBox style={{ marginTop: 10 }}>
                <MissingTitle>
                  {t("sharingAuditIncludedManagers", { count: pasteResult.includedManagerEmails.length })}
                </MissingTitle>
                <ChipRow>
                  {pasteResult.includedManagerEmails.slice(0, 12).map((email) => (
                    <Chip key={email} $variant="included">
                      {permissionGroupsManagerLabelByEmail.get(email) || email}
                    </Chip>
                  ))}
                  {pasteResult.includedManagerEmails.length > 12 && (
                    <MoreText>
                      {t("pasteEmailsMoreUnmatched", { count: pasteResult.includedManagerEmails.length - 12 })}
                    </MoreText>
                  )}
                </ChipRow>
              </MissingBox>
            )}

            {Array.isArray(pasteResult.unmatchedInputs) && pasteResult.unmatchedInputs.length > 0 && (
              <MissingBox style={{ marginTop: 10 }}>
                <MissingTitle>{t("groupSharePasteUnmatchedTitle", { count: pasteResult.unmatchedInputs.length })}</MissingTitle>
                <TextareaSmall value={pasteResult.unmatchedInputs.join("\n")} readOnly />
              </MissingBox>
            )}
          </ResultBox>
        )}

        {Array.isArray(pasteResult?.ambiguousItems) && pasteResult.ambiguousItems.some((it) => !it?.resolved) && (
          <ResultBox hasError={false} style={{ marginTop: 10 }}>
            <ResultLine>{t("groupSharePasteAmbiguousTitle")}</ResultLine>
            <Muted style={{ marginTop: 6 }}>{t("groupSharePasteAmbiguousHint")}</Muted>
            <AmbiguousList>
              {pasteResult.ambiguousItems
                .map((it, idx) => ({ it, idx }))
                .filter(({ it }) => it && !it.resolved)
                .slice(0, 20)
                .map(({ it, idx }) => (
                  <AmbiguousRow key={`${it.input}-${idx}`}>
                    <AmbiguousInput title={it.input}>{it.input}</AmbiguousInput>
                    <AmbiguousControls>
                      <WideDropdown
                        search
                        selection
                        fluid
                        options={it.options || []}
                        value={it.selectedValue || ""}
                        onChange={(e, { value }) => handlePasteAmbiguousPick(idx, value)}
                      />
                      <PrimaryButton
                        type="button"
                        onClick={() => handlePasteAmbiguousApply(idx)}
                        disabled={!it.selectedValue}
                      >
                        {t("groupSharePasteAmbiguousAdd")}
                      </PrimaryButton>
                    </AmbiguousControls>
                  </AmbiguousRow>
                ))}
            </AmbiguousList>
          </ResultBox>
        )}
      </Card>

      <Card>
        <SectionTitle>{t("sharingAuditLoadReportsTitle")}</SectionTitle>
        <Muted>{t("sharingAuditLoadReportsHint")}</Muted>

        <Row>
          <WideDropdown
            multiple
            search
            selection
            fluid
            placeholder={t("groupShareSelectManagersPlaceholder")}
            options={managerGroupOptions}
            value={selectedManagerGroupIds}
            onChange={(e, { value }) => {
              const next = Array.isArray(value) ? value : value ? [value] : [];
              setSelectedManagerGroupIds(next);
            }}
          />
        </Row>

        <Row style={{ marginTop: 10, justifyContent: "space-between" }}>
          <SecondaryButton type="button" onClick={() => setSelectedManagerGroupIds([])} disabled={loadingManagerReports.active}>
            {t("groupShareClearManagers")}
          </SecondaryButton>

          <ButtonRowRight>
            <SecondaryButton
              type="button"
              onClick={() => handleLoadReportsFromManagers({ onlyMissing: true })}
              disabled={
                loadingManagerReports.active ||
                !selectedManagerGroupIds.length ||
                hasUnresolvedAmbiguous ||
                !pasteResult?.missingManagerEmails?.length
              }
            >
              {t("sharingAuditLoadMissingManagers")}
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={() => handleLoadReportsFromManagers({ onlyMissing: false })}
              disabled={loadingManagerReports.active || !selectedManagerGroupIds.length || hasUnresolvedAmbiguous}
            >
              {t("groupShareLoadReportsFromManagers")}
            </PrimaryButton>
          </ButtonRowRight>
        </Row>

        {loadingManagerReports.active && (
          <Meta style={{ marginTop: 10 }}>
            {loadingManagerReports.message} ({loadingManagerReports.done}/{loadingManagerReports.total})
          </Meta>
        )}
        {!loadingManagerReports.active && lastLoadedManagerGroupIds.length > 0 && (
          <Meta style={{ marginTop: 10 }}>
            {t("sharingAuditLoadedManagersCount", { count: lastLoadedManagerGroupIds.length })}
          </Meta>
        )}
      </Card>

      <Card>
        <SectionTitle>{t("sharingAuditReviewTitle")}</SectionTitle>
        <Meta>
          <div>{t("sharingAuditLoadedReportsCount", { count: loadedReports.length })}</div>
          <div>{t("sharingAuditSelectedReportsCount", { count: selectedReportIds.length })}</div>
        </Meta>

        <Row style={{ marginTop: 10 }}>
          <SecondaryButton type="button" onClick={() => setSelectedReportIds([])} disabled={!selectedReportIds.length}>
            {t("groupShareUnselectAllReports")}
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleApplyShares}
            disabled={!targetEmployeeId || selectedReportIds.length === 0 || shareSendState.active}
          >
            {t("sharingAuditApplyShares")}
          </PrimaryButton>
        </Row>

        {(shareSendState.active || shareLastBatchSummary) && (
          <ProgressBox>
            <ProgressTop>
              <ProgressTitle>
                {shareSendState.active && shareSendState.phase === "processing"
                  ? t("groupShareBatchProcessingProgress", {
                      done: shareBatchProgress?.completed || 0,
                      total: shareBatchProgress?.total || shareSendState.total || 0,
                      errors: shareBatchProgress?.errors || 0,
                    })
                  : shareSendState.active
                  ? t("groupShareBatchQueueingProgress", {
                      sent: shareSendState.sent,
                      total: shareSendState.total,
                    })
                  : t("groupShareBatchCompletedInline", {
                      total: shareLastBatchSummary?.total || 0,
                      errors: shareLastBatchSummary?.errors || 0,
                    })}
              </ProgressTitle>
              <ProgressHint>
                {shareSendState.active && shareSendState.phase === "processing"
                  ? t("groupShareBatchProcessingHint")
                  : shareSendState.active
                  ? t("groupShareBatchQueueingHint")
                  : t("groupShareBatchCompletedHint")}
              </ProgressHint>
            </ProgressTop>

            {shareSendState.active && (
              <ProgressList>
                {(shareSendState.items || []).map((item) => {
                  const reportId = Number(item?.reportId);
                  const actionState =
                    Number.isFinite(reportId) && reportId > 0 ? shareActionByReportId?.[reportId] || null : null;

                  const derivedStatus = (() => {
                    if (shareSendState.phase === "queueing") {
                      return item.status === "sent" ? "queued" : "pending";
                    }
                    if (!actionState) return "processing";
                    if (actionState.pending) return "processing";
                    if (actionState.error) return "error";
                    return "done";
                  })();

                  return (
                    <ProgressRow key={`share-${item.reportId}`}>
                      <ProgressName>{item.label || `Report ${item.reportId}`}</ProgressName>
                      <ProgressStatus status={derivedStatus}>
                        {derivedStatus === "done"
                          ? t("batchStatusDone")
                          : derivedStatus === "error"
                          ? t("batchStatusError")
                          : derivedStatus === "queued"
                          ? t("batchStatusQueued")
                          : derivedStatus === "processing"
                          ? t("batchStatusProcessing")
                          : t("batchStatusPending")}
                      </ProgressStatus>
                    </ProgressRow>
                  );
                })}
              </ProgressList>
            )}
          </ProgressBox>
        )}

        {loadedReports.length === 0 ? (
          <Muted style={{ marginTop: 12 }}>{t("groupShareEmptyListHint")}</Muted>
        ) : (
          <>
            {reportBuckets.notAccessible.length > 0 && (
              <>
                <ListHeader>{t("sharingAuditNotAccessibleYet", { count: reportBuckets.notAccessible.length })}</ListHeader>
                <ReportList>
                  {reportBuckets.notAccessible.map((r) => {
                    const rid = Number(r?.id);
                    const checked = selectedReportIds.includes(rid);
                    return (
                      <ReportRow key={rid} onClick={() => toggleReport(rid)} role="button" tabIndex={0}>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleReport(rid)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <ReportText>
                          <ReportName title={getReportDisplayName(r)}>{getReportDisplayName(r)}</ReportName>
                          <ReportMeta>
                            {getReportTemplateLabel(r)} • {formatDate(r?.created_at)}
                          </ReportMeta>
                        </ReportText>
                        <RightTag>{t("sharingAuditWillShare")}</RightTag>
                      </ReportRow>
                    );
                  })}
                </ReportList>
              </>
            )}

            {reportBuckets.accessible.length > 0 && (
              <>
                <ListHeader style={{ marginTop: reportBuckets.notAccessible.length ? 12 : 6 }}>
                  {t("sharingAuditAlreadyAccessible", { count: reportBuckets.accessible.length })}
                </ListHeader>
                <ReportList>
                  {reportBuckets.accessible.map((r) => {
                    const rid = Number(r?.id);
                    const checked = selectedReportIds.includes(rid);
                    return (
                      <ReportRow key={rid} onClick={() => toggleReport(rid)} role="button" tabIndex={0}>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleReport(rid)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <ReportText>
                          <ReportName title={getReportDisplayName(r)}>{getReportDisplayName(r)}</ReportName>
                          <ReportMeta>
                            {getReportTemplateLabel(r)} • {formatDate(r?.created_at)}
                          </ReportMeta>
                        </ReportText>
                        <RightTag $muted>{t("sharingAuditAlreadyHas")}</RightTag>
                      </ReportRow>
                    );
                  })}
                </ReportList>
              </>
            )}
          </>
        )}
      </Card>
    </Page>
  );
};

const Page = styled.div`
  padding: 24px 32px 60px;
  max-width: 1100px;
  margin: 0 auto;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 14px;
`;

const BackButton = styled.button`
  padding: 8px 12px;
  background: transparent;
  color: #1e293b;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: rgba(148, 163, 184, 0.5);
  }
`;

const Header = styled.div`
  margin-bottom: 16px;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  max-width: 800px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  margin-top: 12px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 900;
  color: #0f172a;
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ButtonRowRight = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const WideDropdown = styled(Dropdown)`
  &&& {
    flex: 1;
    min-width: 0;
  }
`;

const PrimaryButton = styled.button`
  padding: 10px 14px;
  background: #111827;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled.button`
  padding: 10px 14px;
  background: transparent;
  color: #475569;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
    color: #0f172a;
    border-color: rgba(148, 163, 184, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Muted = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

const Meta = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Textarea = styled.textarea`
  margin-top: 10px;
  width: 100%;
  min-height: 160px;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  font-size: 13px;
  color: #0f172a;
  font-family: inherit;
  line-height: 1.4;

  &:focus {
    outline: none;
    border-color: #2d70e2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }
`;

const TextareaSmall = styled.textarea`
  margin-top: 10px;
  width: 100%;
  min-height: 120px;
  resize: vertical;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  font-size: 13px;
  color: #0f172a;
  font-family: inherit;
  line-height: 1.4;
  background: #ffffff;
`;

const ResultBox = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(props) => (props.hasError ? "rgba(239, 68, 68, 0.35)" : "rgba(148, 163, 184, 0.22)")};
  background: ${(props) => (props.hasError ? "#fef2f2" : "#f8fafc")};
`;

const ResultLine = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #0f172a;
`;

const MissingBox = styled.div`
  margin-top: 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  padding-top: 10px;
`;

const MissingTitle = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: #0f172a;
`;

const ChipRow = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.div`
  font-size: 12px;
  color: #0f172a;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid
    ${(props) =>
      props.$variant === "missing"
        ? "rgba(239, 68, 68, 0.25)"
        : props.$variant === "included"
        ? "rgba(16, 185, 129, 0.25)"
        : "rgba(148, 163, 184, 0.22)"};
  background: ${(props) =>
    props.$variant === "missing"
      ? "#fef2f2"
      : props.$variant === "included"
      ? "#ecfdf5"
      : "#ffffff"};
`;

const MoreText = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 800;
  padding: 4px 2px;
`;

const AmbiguousList = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AmbiguousRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 10px;
  background: #ffffff;
`;

const AmbiguousInput = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AmbiguousControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ListHeader = styled.div`
  margin-top: 10px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 900;
  color: #0f172a;
`;

const ReportList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 360px;
  overflow: auto;
  padding-right: 4px;
`;

const ReportRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease;

  &:hover {
    background: #f8fafc;
    border-color: rgba(148, 163, 184, 0.3);
  }
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  margin-top: 3px;
`;

const ReportText = styled.div`
  min-width: 0;
  flex: 1;
`;

const ReportName = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReportMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RightTag = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: ${(p) => (p.$muted ? "#64748b" : "#0f172a")};
  padding-top: 1px;
  flex: 0 0 auto;
`;

const Definitions = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DefRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const DefLabel = styled.div`
  font-size: 12px;
  font-weight: 900;
  color: #0f172a;
  white-space: nowrap;
`;

const DefText = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.35;
`;

const CountsRow = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CountChip = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #0f172a;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: #ffffff;
`;

const ProgressBox = styled.div`
  margin-top: 12px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 10px;
  padding: 12px;
  background: #ffffff;
`;

const ProgressTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
`;

const ProgressTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
`;

const ProgressHint = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const ProgressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 220px;
  overflow: auto;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 8px;
`;

const ProgressName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProgressStatus = styled.div`
  font-size: 12px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${(props) => {
    if (props.status === "done") return "#ecfdf5";
    if (props.status === "error") return "#fef2f2";
    if (props.status === "processing") return "#eff6ff";
    if (props.status === "queued") return "#f8fafc";
    return "#f8fafc";
  }};
  color: ${(props) => {
    if (props.status === "done") return "#047857";
    if (props.status === "error") return "#b91c1c";
    if (props.status === "processing") return "#1d4ed8";
    if (props.status === "queued") return "#64748b";
    return "#64748b";
  }};
  border: 1px solid
    ${(props) =>
      props.status === "done"
        ? "rgba(16, 185, 129, 0.25)"
        : props.status === "error"
        ? "rgba(239, 68, 68, 0.3)"
        : props.status === "processing"
        ? "rgba(59, 130, 246, 0.25)"
        : "rgba(148, 163, 184, 0.24)"};
  flex: 0 0 auto;
`;

export default SharingAudit;

